import { call, put, takeEvery } from 'redux-saga/effects';

import notificationActions from '../actions/notificationActions';
import * as api from '../api/notifications';
import * as types from '../constants';
import { logger } from '../logger';

export function* getAlerts(action) {
  try {
    const response = yield call(action.Get, action.apiServiceGetUrl);
    yield put(notificationActions.getAlertsSuccess(response));
  } catch (error) {
    yield put(notificationActions.getAlertsError(error));
  }
}

export function* updateAlert(action) {
  const { alert } = action.payload;
  const { alertId, statusWeb } = alert;

  const requestUrl = { alertId, statusWeb };
  try {
    const response = yield call(action.Patch, requestUrl);
    yield put(notificationActions.updateAlertSuccess(response));
    yield put(notificationActions.getAlerts(action.apiServiceGetUrl, action.Get));
  } catch (error) {
    yield put(notificationActions.updateAlertError(error));
  }
}

export function* getNotificationPreferencesHandler() {
  yield put({ type: types.LOADER });
  try {
    const response = yield call(api.getPreferences);
    yield put({ type: types.UPDATE_USER_PREFERENCES, payload: response });
  } catch (error) {
    logger.error('Error in the Get Request of Notification Preferences', error);
    yield put({ type: types.TURN_LOADER_OFF });
  }
}

export function formatEvents(events, frequencies, toggleState) {
  const frequency = toggleState
    ? frequencies.find(item => item.name === types.FREQUENCY_IMMEDIATELY)
    : frequencies.find(item => item.name === types.FREQUENCY_NEVER);
  return Object.values(events).map(item => ({ ...item, frequency }));
}

export function formatEntities(entities, frequencies, toggleState, id = '') {
  if (!id) {
    return entities.map(item => ({
      ...item,
      isEntityToggled: toggleState,
      events: formatEvents(item.events, frequencies, toggleState)
    }));
  }
  return entities.map(item => {
    if (item.id === id) {
      return { ...item, isEntityToggled: toggleState, events: formatEvents(item.events, frequencies, toggleState) };
    }
    return item;
  });
}

export function formatUserPreferences(
  userPreferences,
  frequencies,
  isNotificationPreferenceEnabled,
  toggleState = '',
  id = ''
) {
  const {
    data: {
      userPreferences: { channels }
    }
  } = userPreferences;
  const updatedChannels = Object.values(channels).map(item => ({
    ...item,
    isChecked: isNotificationPreferenceEnabled,
    entities: id
      ? formatEntities(item.entities, frequencies, toggleState, id)
      : formatEntities(item.entities, frequencies, isNotificationPreferenceEnabled)
  }));
  return { ...userPreferences, ...{ data: { userPreferences: { channels: [...updatedChannels] } } } };
}

export function* toggleHandler(action) {
  yield put({ type: types.LOADER });
  const requestPayload = formatUserPreferences(
    action.userPreferences,
    action.frequencies,
    action.isNotificationPreferenceEnabled,
    action.toggleState,
    action.id
  );
  try {
    const response = yield call(api.sendEnableNotificationToggle, requestPayload);
    yield put({ type: types.UPDATE_USER_PREFERENCES, payload: response });
  } catch (error) {
    logger.error('Error in the Post Request of Notification Preferences', error);
    yield put({ type: types.TURN_LOADER_OFF });
  }
}

export function* getWebNotificationsHandler() {
  yield put({ type: types.LOADER });
  try {
    const {
      data: { notifications }
    } = yield call(api.getWebNotifications);
    yield put({ type: types.UPDATE_WEB_NOTIFICATIONS, payload: notifications });
  } catch (error) {
    logger.error('Error in the Get Request of Web Notifications', error);
    yield put({ type: types.TURN_LOADER_OFF });
  }
}

export function formatReadPayload(id) {
  return [{ notificationId: id, isRead: 1 }];
}

export function formatDeletePayload(notification, deleteAll = false) {
  if (deleteAll) {
    return notification.map(item => ({ notificationId: item.notificationId, isDismissed: 1 }));
  }
  return [{ notificationId: notification, isDismissed: 1 }];
}

export function* webNotificationsDeleteHandler(action) {
  yield put({ type: types.LOADER });
  const requestPayload = action.deleteAll
    ? formatDeletePayload(action.payload, true)
    : formatDeletePayload(action.payload);
  try {
    const {
      data: { status }
    } = yield call(api.patchWebNotifications, requestPayload);
    if (status === types.SUCCESS) {
      const {
        data: { notifications }
      } = yield call(api.getWebNotifications);
      yield put({ type: types.UPDATE_WEB_NOTIFICATIONS, payload: notifications });
    }
  } catch (error) {
    logger.error('Error in the Post Request of Web Notifications', error);
    yield put({ type: types.TURN_LOADER_OFF });
  }
}

export function* webNotificationsReadHandler(action) {
  const requestPayload = formatReadPayload(action.payload);
  try {
    if (action.isNotificationAlreadyRead === 0) {
      yield put({ type: types.LOADER });
      const {
        data: { status }
      } = yield call(api.patchWebNotifications, requestPayload);
      if (action.callBack && status === types.SUCCESS) {
        action.callBack();
        yield put({ type: types.TURN_LOADER_OFF });
      }
    } else {
      action.callBack();
    }
  } catch (error) {
    logger.error('Error in the Post Request of Web Notifications', error);
    yield put({ type: types.TURN_LOADER_OFF });
  }
}

export function* webNotificationReadExceptionHandler(action) {
  yield put({ type: types.LOADER });
  const { payload: notificationId, notifications } = action;
  try {
    if (notifications && notifications.length && notificationId) {
      const filteredNotifications = notifications.map(item => {
        if (item.notificationId === notificationId) {
          return { ...item, read: 1 };
        }
        return item;
      });
      yield put({ type: types.UPDATE_WEB_NOTIFICATIONS, payload: filteredNotifications });
    } else {
      yield put({ type: types.UPDATE_WEB_NOTIFICATIONS, payload: notifications });
    }
  } catch (error) {
    logger.error('Error in the Web Notifications', error);
    yield put({ type: types.TURN_LOADER_OFF });
  }
}

// the 'watcher' - on every 'GET_ALERTS' action, run our get alerts function

export function* notificationWatcher() {
  yield takeEvery(types.GET_ALERTS, getAlerts);
  yield takeEvery(types.UPDATE_ALERT, updateAlert);
  yield takeEvery(types.GET_NOTIFICATION_PREFERENCES, getNotificationPreferencesHandler);
  yield takeEvery(types.UPDATE_ENABLE_NOTIFICATIONS_TOGGLE, toggleHandler);
  yield takeEvery(types.UPDATE_TOGGLE_STATE, toggleHandler);
  yield takeEvery(types.FETCH_WEB_NOTIFICATIONS, getWebNotificationsHandler);
  yield takeEvery(types.DELETE_NOTIFICATION, webNotificationsDeleteHandler);
  yield takeEvery(types.DELETE_ALL_NOTIFICATIONS, webNotificationsDeleteHandler);
  yield takeEvery(types.NOTIFICATION_READ, webNotificationsReadHandler);
  yield takeEvery(types.NOTIFICATION_READ_EXCEPTION, webNotificationReadExceptionHandler);
}
